import { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/react'
import { CheckIcon } from '@chakra-ui/icons'
import { glassCard } from '../styles'
import { apiFetch } from '../utils/apiFetch'

interface StoredUser {
  name?: string
  picture?: string
  email?: string
  isAdmin?: boolean
  title?: string
  joinDate?: string
}

function toDateInput(iso?: string): string {
  if (!iso) return ''
  return iso.slice(0, 10)
}

export default function Settings() {
  const [user, setUser] = useState<StoredUser | null>(() => {
    const data = localStorage.getItem('user')
    return data ? JSON.parse(data) : null
  })
  const [displayName, setDisplayName] = useState(user?.name ?? '')
  const [title, setTitle] = useState(user?.title ?? '')
  const [joinDate, setJoinDate] = useState(toDateInput(user?.joinDate))
  const [phone, setPhone] = useState('')
  const [dietary, setAllergies] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Always load fresh profile from the server on mount
  useEffect(() => {
    apiFetch('/api/auth/me')
      .then(r => r.json())
      .then(data => {
        setDisplayName(data.name ?? user?.name ?? '')
        setTitle(data.title ?? '')
        setJoinDate(toDateInput(data.joinDate))
        setPhone(data.phone ?? '')
        setAllergies(data.dietary ?? '')
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    setSaving(true)
    const body = {
      name: displayName.trim() || null,
      title: title.trim() || null,
      joinDate: joinDate || null,
      phone: phone.trim() || null,
      dietary: dietary.trim() || null,
    }

    const res = await apiFetch('/api/auth/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (res.ok) {
      const data = await res.json()
      const updated: StoredUser = {
        ...user,
        name: data.name ?? user?.name,
        title: data.title ?? undefined,
        joinDate: data.joinDate ?? undefined,
      }
      localStorage.setItem('user', JSON.stringify(updated))
      setUser(updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    }
    setSaving(false)
  }

  const inputStyle = { color: 'white', borderColor: 'whiteAlpha.300', _placeholder: { color: 'whiteAlpha.400' } }

  return (
    <Box minH="100vh" position="relative" p={{ base: 4, md: 8 }} pt={{ base: 16, md: 20 }}>

      <Box maxW="500px" mx="auto">
        <VStack spacing={6} align="stretch">

          <Text fontSize="2xl" fontWeight="bold" color="white">⚙️ Settings</Text>

          <Box {...glassCard} borderRadius="xl" p={{ base: 4, md: 6 }}>
            <VStack spacing={5} align="stretch">
              <Text fontWeight="semibold" color="white" fontSize="lg">Profile</Text>

              <FormControl>
                <FormLabel color="whiteAlpha.700" fontSize="sm">Display Name</FormLabel>
                <Input
                  value={displayName}
                  onChange={e => { setDisplayName(e.target.value); setSaved(false) }}
                  placeholder={user?.email ?? 'Your name'}
                  {...inputStyle}
                />
                <FormHelperText color="whiteAlpha.500" fontSize="xs">
                  Shown instead of your Google account name across the app.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel color="whiteAlpha.700" fontSize="sm">Club Title</FormLabel>
                <Input
                  value={title}
                  onChange={e => { setTitle(e.target.value); setSaved(false) }}
                  placeholder="e.g. Beer Enthusiast"
                  {...inputStyle}
                />
                <FormHelperText color="whiteAlpha.500" fontSize="xs">
                  Displayed on your dashboard profile card.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel color="whiteAlpha.700" fontSize="sm">Member Since</FormLabel>
                <Input
                  type="date"
                  value={joinDate}
                  onChange={e => { setJoinDate(e.target.value); setSaved(false) }}
                  {...inputStyle}
                  sx={{ '::-webkit-calendar-picker-indicator': { filter: 'invert(1)' } }}
                />
                <FormHelperText color="whiteAlpha.500" fontSize="xs">
                  Used to calculate how long you've been in the club.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel color="whiteAlpha.700" fontSize="sm">Phone Number</FormLabel>
                <Input
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setSaved(false) }}
                  placeholder="+46 70 000 00 00"
                  {...inputStyle}
                />
                <FormHelperText color="whiteAlpha.500" fontSize="xs">
                  Visible to all members on your profile page.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel color="whiteAlpha.700" fontSize="sm">Allergies / Dietary needs</FormLabel>
                <Textarea
                  value={dietary}
                  onChange={e => { setAllergies(e.target.value); setSaved(false) }}
                  placeholder="e.g. Gluten-free, no nuts"
                  {...inputStyle}
                  rows={2}
                />
                <FormHelperText color="whiteAlpha.500" fontSize="xs">
                  Visible to all members on your profile page.
                </FormHelperText>
              </FormControl>

              <HStack justify="flex-end">
                {saved && (
                  <HStack spacing={1} color="green.300">
                    <CheckIcon boxSize={3} />
                    <Text fontSize="sm">Saved</Text>
                  </HStack>
                )}
                <Button
                  size="sm"
                  colorScheme="orange"
                  isLoading={saving}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          </Box>

        </VStack>
      </Box>
    </Box>
  )
}
